/**
 * Book screen (chat-centric revamp §4): the portfolio tab.
 *
 * Structure: KPI strip (total value, day-Δ with stale-price disclosure,
 * unrealized/realized P/L) → in-screen sub-tabs Positions / Watchlist /
 * Trades (mirroring PortfolioTab's segmented-control pattern — PortfolioTab
 * itself is not reused; its thesis sub-tab is dissolved: the portfolio thesis
 * form opens from the ＋ sheet instead) → a collapsible analytics section on
 * the positions pane (theme exposure, the four allocation pies, Risk and
 * Calibration links — the DashboardNavigator routes stay registered on the
 * root stack per Task 5, so the links navigate by nested name).
 *
 * The ＋ sheet opens the existing manual forms (Add Trade lives under the
 * PortfolioNavigator, the watchlist entry lands on the watchlist pane's
 * capture row, the portfolio thesis form mounts inline). The ⋯ sheet exposes
 * 经典导入: the legacy ImportButton flow (pick → upload → document review),
 * mounted hidden and triggered programmatically exactly as PortfolioTab does.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  SafeAreaView,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../contexts/ThemeContext';
import {
  PortfolioQualifierContext,
  type PortfolioNavigateFn,
} from '../../navigation/PortfolioQualifierContext';
import { getDashboardData } from '../../services/dashboardApi';
import type { DashboardData } from '../../services/dashboardApi';
import { usePositions } from '../../services/api/queries/usePortfolio';
import { PositionTable } from '../../components/portfolio/PositionTable';
import { WatchlistScreen } from '../watchlist/WatchlistScreen';
import { TradesScreen } from '../portfolio/TradesScreen';
import { PortfolioThesisForm } from '../../components/portfolio/PortfolioThesisForm';
import { ImportButton } from '../../components/portfolio/ImportButton';
import { KpiStrip } from '../../components/book/KpiStrip';
import { AnalyticsSection } from '../../components/book/AnalyticsSection';

type BookTab = 'positions' | 'watchlist' | 'trades';

export function BookScreen() {
  const navigation = useNavigation<any>();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [activeTab, setActiveTab] = useState<BookTab>('positions');
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [showPortfolioThesis, setShowPortfolioThesis] = useState(false);
  const [importTrigger, setImportTrigger] = useState<'csv' | 'image' | 'pdf' | 'menu' | null>(null);

  const positionsQuery = usePositions();
  const positions = useMemo(
    () => (positionsQuery.data ?? []).filter((p) => Number(p.quantity) !== 0),
    [positionsQuery.data]
  );

  const loadDashboard = useCallback(async () => {
    try {
      setDashboard(await getDashboardData());
    } catch (error) {
      // The KPI strip degrades to placeholders; the sub-tab content below
      // still works off its own queries.
      console.error('Failed to load dashboard:', (error as Error)?.message);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Reset the import trigger after it has been handed to ImportButton, so the
  // same entry can fire twice (mirrors PortfolioTab).
  useEffect(() => {
    if (importTrigger) {
      const timer = setTimeout(() => setImportTrigger(null), 100);
      return () => clearTimeout(timer);
    }
  }, [importTrigger]);

  const navigateToRisk = useCallback(
    () => navigation.navigate('Dashboard', { screen: 'Risk' }),
    [navigation]
  );
  const navigateToCalibration = useCallback(
    () => navigation.navigate('Dashboard', { screen: 'Calibration' }),
    [navigation]
  );

  // The embedded portfolio components navigate by bare PortfolioNavigator
  // screen names, which are dead outside the legacy Portfolio tab — qualify
  // them through the root-registered Portfolio navigator (Task 9 sweep).
  const qualifyPortfolioNav = useCallback<PortfolioNavigateFn>(
    (screen, params) => navigation.navigate('Portfolio', { screen, params }),
    [navigation]
  );

  const subTabs: { key: BookTab; label: string }[] = [
    { key: 'positions', label: t('portfolio.positions') },
    { key: 'watchlist', label: t('watchlist.title') },
    { key: 'trades', label: t('portfolio.trades') },
  ];

  const renderContent = () => {
    if (showPortfolioThesis) {
      return (
        <View style={styles.thesisContainer}>
          <View style={styles.thesisHeader}>
            <Text style={styles.thesisTitle}>{t('portfolio.portfolioThesis')}</Text>
            <TouchableOpacity
              style={styles.thesisClose}
              onPress={() => setShowPortfolioThesis(false)}
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              testID="book-thesis-close"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.thesisCloseText}>✕</Text>
            </TouchableOpacity>
          </View>
          <PortfolioThesisForm />
        </View>
      );
    }
    if (activeTab === 'watchlist') {
      // WatchlistScreen owns its FlatList — render it directly, never inside
      // the positions ScrollView, to avoid nested virtualized lists.
      return <WatchlistScreen />;
    }
    if (activeTab === 'trades') {
      // TradesScreen likewise owns its own list and data loading.
      return <TradesScreen />;
    }
    return (
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {positionsQuery.isLoading ? (
          <ActivityIndicator style={styles.positionsLoader} color={theme.colors.primary} />
        ) : (
          <PositionTable positions={positions} />
        )}
        <AnalyticsSection
          data={dashboard}
          onNavigateRisk={navigateToRisk}
          onNavigateCalibration={navigateToCalibration}
        />
      </ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{t('tabs.book', { defaultValue: 'Book' })}</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowAddSheet(true)}
            accessibilityRole="button"
            accessibilityLabel={t('book.addOptions')}
            testID="book-add-button"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={styles.headerButtonText}>＋</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowMoreSheet(true)}
            accessibilityRole="button"
            accessibilityLabel={t('book.moreOptions')}
            testID="book-more-button"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={styles.headerButtonText}>⋯</Text>
          </TouchableOpacity>
        </View>
      </View>

      <KpiStrip data={dashboard} positions={positions} />

      <View style={styles.subTabs}>
        {subTabs.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.subTab, activeTab === tab.key && styles.subTabActive]}
            onPress={() => {
              setActiveTab(tab.key);
              setShowPortfolioThesis(false);
            }}
            accessibilityRole="button"
            testID={`book-subtab-${tab.key}`}
          >
            <Text style={[styles.subTabText, activeTab === tab.key && styles.subTabTextActive]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <PortfolioQualifierContext.Provider value={qualifyPortfolioNav}>
        {renderContent()}
      </PortfolioQualifierContext.Provider>

      {/* ＋ sheet: the three manual entry points. */}
      <Modal
        visible={showAddSheet}
        transparent
        animationType="slide"
        onRequestClose={() => setShowAddSheet(false)}
      >
        <TouchableOpacity
          style={styles.sheetOverlay}
          activeOpacity={1}
          onPress={() => setShowAddSheet(false)}
        >
          <View
            style={[styles.sheetContent, { backgroundColor: theme.colors.surface }]}
            testID="book-add-sheet"
          >
            <TouchableOpacity
              style={styles.sheetRow}
              onPress={() => {
                setShowAddSheet(false);
                navigation.navigate('Portfolio', { screen: 'AddTrade', params: {} });
              }}
              accessibilityRole="button"
            >
              <Text style={styles.sheetRowText}>{t('portfolio.addTrade')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.sheetRow}
              onPress={() => {
                setShowAddSheet(false);
                setActiveTab('watchlist');
                setShowPortfolioThesis(false);
              }}
              accessibilityRole="button"
            >
              <Text style={styles.sheetRowText}>{t('book.addToWatchlist')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.sheetRow}
              onPress={() => {
                setShowAddSheet(false);
                setShowPortfolioThesis(true);
              }}
              accessibilityRole="button"
            >
              <Text style={styles.sheetRowText}>{t('portfolio.portfolioThesis')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.sheetRow, styles.sheetCancelRow]}
              onPress={() => setShowAddSheet(false)}
              accessibilityRole="button"
            >
              <Text style={[styles.sheetRowText, styles.sheetCancelText]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ⋯ sheet: the legacy import entry. */}
      <Modal
        visible={showMoreSheet}
        transparent
        animationType="slide"
        onRequestClose={() => setShowMoreSheet(false)}
      >
        <TouchableOpacity
          style={styles.sheetOverlay}
          activeOpacity={1}
          onPress={() => setShowMoreSheet(false)}
        >
          <View style={[styles.sheetContent, { backgroundColor: theme.colors.surface }]}>
            <TouchableOpacity
              style={styles.sheetRow}
              onPress={() => {
                setShowMoreSheet(false);
                setImportTrigger('menu');
              }}
              accessibilityRole="button"
            >
              <Text style={styles.sheetRowText}>{t('tabs.classicImport')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.sheetRow, styles.sheetCancelRow]}
              onPress={() => setShowMoreSheet(false)}
              accessibilityRole="button"
            >
              <Text style={[styles.sheetRowText, styles.sheetCancelText]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Programmatic trigger only: overflow from this zero-sized wrapper must
          never participate in hit testing or it can open the import modal. */}
      <View style={styles.hiddenImportButton} pointerEvents="none">
        <ImportButton triggerImport={importTrigger} onUploadComplete={loadDashboard} />
      </View>
    </SafeAreaView>
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.lg,
    },
    headerButton: { padding: theme.spacing.xs },
    headerButtonText: {
      fontSize: theme.typography.fontSize2Xl || theme.typography.fontSizeXl,
      color: theme.colors.primary,
      fontWeight: theme.typography.fontWeightBold,
    },
    subTabs: {
      flexDirection: 'row',
      marginHorizontal: theme.spacing.lg,
      marginTop: theme.spacing.md,
      backgroundColor: theme.colors.backgroundElevated,
      borderRadius: theme.radii.md,
    },
    subTab: {
      flex: 1,
      paddingVertical: theme.spacing.sm,
      alignItems: 'center',
      borderRadius: theme.radii.md,
    },
    subTabActive: {
      backgroundColor: theme.colors.primary,
    },
    subTabText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textSecondary,
    },
    subTabTextActive: {
      color: theme.colors.textInverse,
    },
    content: {
      flex: 1,
      marginTop: theme.spacing.md,
    },
    contentContainer: {
      paddingBottom: theme.spacing.xl,
    },
    positionsLoader: {
      marginTop: theme.spacing.xl,
    },
    thesisContainer: {
      flex: 1,
    },
    thesisHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.sm,
    },
    thesisTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    thesisClose: { padding: theme.spacing.xs },
    thesisCloseText: {
      fontSize: theme.typography.fontSizeLg,
      color: theme.colors.textSecondary,
    },
    sheetOverlay: {
      flex: 1,
      backgroundColor: theme.colors.overlay,
      justifyContent: 'flex-end',
    },
    sheetContent: {
      borderTopLeftRadius: theme.radii.xl,
      borderTopRightRadius: theme.radii.xl,
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.lg,
      paddingBottom: theme.spacing.xl,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    sheetRow: {
      paddingVertical: theme.spacing.md,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    sheetRowText: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.text,
    },
    sheetCancelRow: {
      borderBottomWidth: 0,
    },
    sheetCancelText: {
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    hiddenImportButton: {
      position: 'absolute',
      width: 0,
      height: 0,
      opacity: 0,
    },
  });
}
